import { memo, MouseEvent as ReactMouseEvent, useState } from 'react';
import { Card, IconButton, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import type { DataGridProProps, GridColDef, GridRowParams, GridValidRowModel } from '@mui/x-data-grid-pro';
import { CardContent, CardContentProps } from './CardContent';
import { mergeSx } from '../../../utils/styles';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import type { MobileCardHeaderItems } from './types';
import { ExpandMore } from '@mui/icons-material';
import { MobileCardHeader } from '../../molecules/MobileCardHeader';

const styles = {
  card: {
    backgroundColor: 'background.light',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
  },
  totalCard: {
    backgroundColor: 'background.light',
    fontWeight: 600,
  },
  totalLabel: {
    typography: 'subtitle1',
    color: 'text.primary',
    py: 2,
    ml: 'auto',
  },
  accordion: {
    backgroundColor: 'background.light',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    overflow: 'hidden',
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: 0,
    },
  },
  accordionSummary: {
    pl: 1.5,
    pr: 1,
    gap: 1,
    minHeight: 44,
    '&.Mui-expanded': {
      minHeight: 44,
    },
    '& .MuiAccordionSummary-content': {
      m: 0,
      minWidth: 0,
      overflow: 'hidden',
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      flexShrink: 0,
    },
  },
  accordionDetails: {
    p: 0,
    borderTop: '1px solid',
    borderColor: 'divider',
  },
  expandIcon: {
    display: 'block',
    ml: 'auto',
    mb: 1,
    mr: 1,
    color: 'primary.main',
  },
};

const totalsRowHeaderItems = [
  {
    value: 'Totals',
    align: 'right' as const,
    flex: 1,
    variant: 'h6' as const,
    fontWeight: 600,
  },
];

export type MobileCellClick<R extends GridValidRowModel> = (
  params: { id: string | number; field: string; row: R; value: unknown; colDef: GridColDef<R> },
  event: React.MouseEvent<HTMLElement> | undefined
) => void;

export type RowCardProps<R extends GridValidRowModel> = CardContentProps<R> & {
  onRowClick?: DataGridProProps['onRowClick'];
  isTotalsRow?: boolean;
  processRowUpdate?: DataGridProProps<R>['processRowUpdate'];
  mobileCardHeaderItems?: MobileCardHeaderItems<R>;
};

const RowCardComponent = <R extends GridValidRowModel>(props: RowCardProps<R>) => {
  const { onRowClick, rowId, row, columns, isTotalsRow, mobileCardHeaderItems } = props;
  const [isExpanded, setIsExpanded] = useState(isTotalsRow);

  const handleCardClick = (e: ReactMouseEvent<HTMLElement>) => {
    if (!onRowClick) return;

    const params: GridRowParams<R> = {
      id: rowId,
      row,
      columns: columns as GridColDef<R>[],
    };

    onRowClick(params, e, { api: null as never });
  };

  const enableAccordion = mobileCardHeaderItems || isTotalsRow;

  if (enableAccordion) {
    return (
      <Accordion
        elevation={0}
        expanded={isExpanded}
        onChange={() => setIsExpanded(!isExpanded)}
        sx={styles.accordion}
        disableGutters
        slotProps={{ transition: { unmountOnExit: true } }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore fontSize='small' />}
          sx={mergeSx(styles.accordionSummary, {
            backgroundColor: isTotalsRow ? 'background.lighter' : 'background.light',
          })}
        >
          <MobileCardHeader items={isTotalsRow ? totalsRowHeaderItems : mobileCardHeaderItems?.(row) || []} />
        </AccordionSummary>
        <AccordionDetails sx={styles.accordionDetails}>
          <CardContent {...props} />
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <Card elevation={0} sx={mergeSx(styles.card, isTotalsRow ? styles.totalCard : undefined)}>
      <CardContent {...props} />
      {onRowClick && (
        <IconButton onClick={handleCardClick} sx={styles.expandIcon}>
          <KeyboardDoubleArrowRightIcon />
        </IconButton>
      )}
    </Card>
  );
};

export const RowCard = memo(RowCardComponent) as typeof RowCardComponent;
